/**
 * The portal registry, loaded once for the whole shell:
 *   apps   — GET /api/apps/   (pages this user may open; filtered server-side)
 *   groups — GET /api/groups/ (labels/icons for the profile dropdown)
 *
 * Both are managed in Portal Admin. reload() refetches them; IframeView calls
 * it when Portal Admin says it changed the registry (VQ_REGISTRY_CHANGED).
 * A reload keeps the current data on screen — `loading` is true only for the
 * very first load — so an open iframe is never unmounted by a refresh.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { apiFetch } from "./client";

const RegistryContext = createContext(null);

// /api/groups/ answers with the repo envelope {status, message, error, data};
// /api/apps/ sends the bare array. Return the value either way.
const unwrap = (body) =>
  body && typeof body === "object" && "status" in body && "data" in body ? body.data : body;

export function RegistryProvider({ children }) {
  const { instance, inProgress, accounts } = useMsal();
  const [state, setState] = useState({ apps: null, groups: null, loading: true, error: null });

  const load = useCallback(async () => {
    const [apps, groups] = await Promise.allSettled([
      apiFetch(instance, "/api/apps/"),
      apiFetch(instance, "/api/groups/").then(unwrap),
    ]);
    setState((prev) => ({
      // keep the last good copy if a refresh fails
      apps: apps.status === "fulfilled" ? apps.value : prev.apps,
      groups: groups.status === "fulfilled" && Array.isArray(groups.value) ? groups.value : prev.groups,
      loading: false,
      error: apps.status === "rejected" ? apps.reason.message : null,
    }));
  }, [instance]);

  // Same guards as useApi: wait for MSAL to settle and an account to be restored.
  const ready = inProgress === InteractionStatus.None && accounts.length > 0;
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  return (
    <RegistryContext.Provider value={{ ...state, reload: load }}>
      {children}
    </RegistryContext.Provider>
  );
}

export function useRegistry() {
  return useContext(RegistryContext);
}
